import { useState, useRef, useEffect } from 'react';
import * as Sentry from '@sentry/react';

function getLocalStorage(): Storage | null {
  try {
    return localStorage;
  } catch {
    return null;
  }
}

function initState<T>(defaultValue: T) {
  try {
    const state = getLocalStorage()?.getItem('state');
    if (state) {
      return JSON.parse(state) as T;
    }
  } catch {
    return defaultValue;
  }
  return defaultValue;
}

const useCrossTabState = <T>(stateKey: string, defaultValue: T) => {
  const [state, setState] = useState<T | unknown>(() => initState(defaultValue));
  const isNewSession = useRef(true);
  useEffect(() => {
    const storage = getLocalStorage();
    if (isNewSession.current) {
      try {
        const currentState = storage?.getItem(stateKey);
        if (currentState) {
          setState(JSON.parse(currentState));
        } else {
          setState(defaultValue);
        }
      } catch {
        setState(defaultValue);
      }
      isNewSession.current = false;
      return;
    }
    try {
      storage?.setItem(stateKey, JSON.stringify(state));
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTags({
          message: `Error trying to set LocalStorage ${stateKey}, ${JSON.stringify(state)}`,
          file: '?',
          fn: 'useCrossTabState()-01',
        });
        Sentry.captureException(error);
      });
    }
  }, [state, stateKey, defaultValue]);

  useEffect(() => {
    const onReceiveMessage = () => {
      try {
        const valueFromStorage = JSON.parse(getLocalStorage()?.getItem(stateKey) ?? '');
        setState((oldState: T | unknown) =>
          valueFromStorage === oldState ? oldState : valueFromStorage,
        );
      } catch {
        // Ignore a missing storage global or a value that is not valid JSON.
      }
    };
    window.addEventListener('storage', onReceiveMessage, false);
    return () => window.removeEventListener('storage', onReceiveMessage);
  }, [stateKey, setState]);

  const updateState = (newState: T) => {
    setState(newState);
    try {
      getLocalStorage()?.setItem(stateKey, JSON.stringify(newState));
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTags({
          message: `Error trying to set LocalStorage ${stateKey}, ${JSON.stringify(newState)}`,
          file: '?',
          fn: 'useCrossTabState()-02',
        });
        Sentry.captureException(error);
      });
    }
  };

  const clearLocalStorage = () => {
    try {
      getLocalStorage()?.removeItem(stateKey);
    } catch {
      // localStorage is missing in some in-app browsers, such as Apple Mail.
    }
  };
  return [state, updateState, clearLocalStorage] as const;
};

export default useCrossTabState;
